'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import {
  Loader2,
  Trash2,
  Truck,
  Clock,
  Package as PackageIcon
} from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SHIPPING_HASH } from '../constants';
import {
  useBulkDeleteShipmentsMutation,
  useBulkChangeStatusMutation
} from '../services/shipping.mutations';
import type { Shipment, ShipmentStatus } from '../types';

interface ShippingTableActionBarProps {
  table: Table<Shipment>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function ShippingTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: ShippingTableActionBarProps) {
  const t = useTranslations('shipping');
  const bulkDelete = useHashDialog(SHIPPING_HASH.bulkDelete);

  const bulkDeleteMutation = useBulkDeleteShipmentsMutation();
  const bulkChangeStatusMutation = useBulkChangeStatusMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeleteMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    bulkDelete.onOpenChange(false);
  }

  async function handleBulkChangeStatus(status: ShipmentStatus) {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkChangeStatusMutation.mutateAsync({
          all: true,
          filters: payload.filters,
          status
        });
      } else {
        await bulkChangeStatusMutation.mutateAsync({
          ids: payload.ids,
          status
        });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
  }

  const statusOptions: {
    value: ShipmentStatus;
    label: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: 'ready_for_shipping',
      label: t('statusReadyForShipping'),
      icon: <Truck className='me-2 h-4 w-4' />
    },
    {
      value: 'in_transit',
      label: t('statusInTransit'),
      icon: <PackageIcon className='me-2 h-4 w-4' />
    },
    {
      value: 'draft',
      label: t('statusDraft'),
      icon: <Clock className='me-2 h-4 w-4' />
    }
  ];

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => bulkDelete.onOpenChange(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                disabled={bulkChangeStatusMutation.isPending}
              >
                {bulkChangeStatusMutation.isPending ? (
                  <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Truck className='me-1.5 h-3.5 w-3.5' />
                )}
                {t('bulkChangeStatus')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start'>
              {statusOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleBulkChangeStatus(option.value)}
                >
                  {option.icon}
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AlertDialog
        open={bulkDelete.open}
        onOpenChange={bulkDelete.onOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
